import { consola } from 'consola'
import { ref, computed, watch } from 'vue'
import { useCheckoutFormStore } from './useCheckoutFormStore'
import { useGiftTiers } from './useGiftTiers'
import { useCartStore } from '@entities/order'
import { useInstitutionStore } from '@entities/institution'
import { useUserProfileStore } from '@entities/user'
import { useApi } from '@shared/api'
import { getClientFingerprint } from '@shared/lib/auth'
import { useAnalytics } from '@shared/lib'
import { useAlertStore } from '@shared/model'
import { useAuthState } from '~/entities/auth'

interface OrderItemForPromocode {
  productId: string
  price: number
  quantity: number
  categoryId?: string // ← ID категории (для промокодов на категории)
}

interface ApplyPromocodeDto {
  code: string
  userId: string
  orderId: string
  institutionId: string
  orderAmount: number
  orderType: 'delivery' | 'takeaway' | 'dine_in'
  branchId?: string
  zoneId?: string
  productIds?: string[]
  items?: OrderItemForPromocode[] // ← Детали товаров для расчета скидки
  categoryIds?: string[]
  isUsingLoyalty?: boolean
  ipAddress?: string
  deviceId?: string
  /** Отпечаток устройства — по нему сервер считает лимит max_usage_per_device. */
  clientFingerprint?: string
  userAgent?: string
}

interface ValidatePromocodeDto {
  code: string
  userId: string
  institutionId: string
  orderAmount: number
  orderType: 'delivery' | 'takeaway' | 'dine_in'
  branchId?: string
  zoneId?: string
  productIds?: string[]
  items?: OrderItemForPromocode[] // ← Детали товаров для расчета скидки
  categoryIds?: string[]
  isUsingLoyalty?: boolean
}

interface PromocodeResult {
  valid: boolean
  discountAmount: number
  finalAmount: number
  freeDelivery: boolean
  loyaltyPointsAwarded?: number
  promocodeId?: string
  errorMessage?: string
  compatibleWithLoyalty?: boolean
  // Для gift_product: тип скидки и подарки — клиент сам кладёт подарок в корзину.
  discountType?: string
  giftProductId?: string | null
  giftProductIds?: string[] // Цепочка подарков (основной + план Б): берём первый доступный
}

interface PromocodeError {
  statusCode: number
  message: string
  code: string
}

/**
 * Composable для работы с промокодами
 */
export function usePromocode() {
  const api = useApi(true) // используем API v2
  const cartStore = useCartStore()
  const checkoutFormStore = useCheckoutFormStore()
  const institutionStore = useInstitutionStore()
  const userProfileStore = useUserProfileStore()
  const { isLoggedIn } = useAuthState()
  const alertStore = useAlertStore()
  const giftTiers = useGiftTiers()
  const { track } = useAnalytics()

  /**
   * База промокода: корзина за вычетом одной единицы подарка «за сумму».
   *
   * Подарок лежит в корзине по полной цене, но гость за него не платит, поэтому
   * процент промокода на него не начисляется — так считают и `orders/calculate`, и
   * создание заказа (a51676c9). Если слать сюда полную сумму корзины, бэкенд вернёт
   * скидку больше той, что реально применит касса, а фронт на этой завышенной цифре
   * посчитает базу порогов подарка и покажет неверное «добери ещё N ₽».
   */
  const promocodeBaseAmount = computed(() =>
    Math.max(0, cartStore.totalPrice - giftTiers.giftLinePrice.value),
  )

  // State
  const promocode = ref('')
  const isApplied = ref(false)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const appliedPromocode = ref<PromocodeResult | null>(null)

  // Состояние подарка (gift_product): что за товар положили и был ли он уже в корзине
  // до применения промокода (если да — при снятии промокода НЕ удаляем, только снимаем метку).
  const giftState = ref<{ productId: string; wasAlreadyInCart: boolean } | null>(null)

  /**
   * Владение подарком промокода переживает перезагрузку.
   *
   * Сервер не хранит isGift, а промокод после F5 не восстанавливается — подарок
   * оставался в корзине обычной ПЛАТНОЙ строкой, и гость молча платил за то, что
   * ему подарили. Достаточно было просто обновить страницу.
   *
   * Тот же приём, что у подарка за сумму (`giftTierOwnership`) и подарка за
   * частоту (`weeklyGiftOwnership`).
   */
  const GIFT_OWNERSHIP_KEY = 'promocodeGiftOwnership'

  function readGiftOwnership(): { productId: string; wasAlreadyInCart: boolean } | null {
    if (!import.meta.client) return null
    try {
      const raw = localStorage.getItem(GIFT_OWNERSHIP_KEY)
      if (!raw) return null
      const parsed = JSON.parse(raw)
      return typeof parsed?.productId === 'string'
        ? { productId: parsed.productId, wasAlreadyInCart: !!parsed.wasAlreadyInCart }
        : null
    } catch {
      return null
    }
  }

  function writeGiftOwnership(state: { productId: string; wasAlreadyInCart: boolean } | null): void {
    if (!import.meta.client) return
    try {
      if (state === null) localStorage.removeItem(GIFT_OWNERSHIP_KEY)
      else localStorage.setItem(GIFT_OWNERSHIP_KEY, JSON.stringify(state))
    } catch {
      // Приватный режим — переживём без персистентности
    }
  }

  /** Запоминает владение и в состоянии, и на диске — источник один. */
  function setGiftState(state: { productId: string; wasAlreadyInCart: boolean } | null): void {
    giftState.value = state
    writeGiftOwnership(state)
  }

  /** Ищет товар (single или вариант group) в меню заведения по id. */
  function findGiftProduct(productId: string): any {
    const id = Number(productId)
    if (!id || Number.isNaN(id)) return null
    for (const category of institutionStore.products || []) {
      if (!category.products) continue
      for (const product of category.products) {
        if (product.type === 'single' && Number(product.id) === id) return product
        if (product.type === 'group' && product.variants) {
          const variant = product.variants.find((v: any) => Number(v.id) === id)
          if (variant) return variant
        }
      }
    }
    return null
  }

  /**
   * Собирает цепочку подарков (основной + план Б) из ответа валидации.
   */
  function giftChainOf(result: PromocodeResult): string[] {
    if (result.giftProductIds && result.giftProductIds.length > 0) {
      return result.giftProductIds.map(String)
    }
    return result.giftProductId ? [String(result.giftProductId)] : []
  }

  /**
   * Для валидного gift_product-промокода кладёт подарочное блюдо в корзину и помечает его.
   * Берёт ПЕРВЫЙ доступный подарок из цепочки (есть в меню и не на стопе) — план Б.
   * Идемпотентно: если подарок из цепочки уже в корзине — только помечает (без дублей),
   * поэтому повторный вызов из ре-валидации не создаёт петли.
   *
   * @returns true — не gift-промокод ИЛИ подарок размещён/уже в корзине;
   *          false — gift-промокод, но НИ ОДИН подарок из цепочки недоступен (промокод не применять).
   */
  async function syncGiftInCart(result: PromocodeResult): Promise<boolean> {
    if (result.discountType !== 'gift_product') return true

    const chain = giftChainOf(result)
    if (chain.length === 0) return true // подарок не задан — не блокируем (касса разберётся)

    const branchId = Number(institutionStore.selectedBranch?.id)
    if (!branchId) return true // без филиала не резолвим — не блокируем применение

    // Уже есть в корзине какой-то подарок из цепочки?
    const existing = cartStore.cart.find(item => chain.includes(String(item.id)))
    if (existing) {
      if (giftState.value === null) {
        // Владение могло остаться с прошлой сессии: тогда позицию положили МЫ,
        // и «уже была в корзине» ставить нельзя — иначе при снятии промокода
        // наш же подарок останется платным.
        const remembered = readGiftOwnership()
        const owned = remembered?.productId === String(existing.id) && !remembered.wasAlreadyInCart
        setGiftState({ productId: String(existing.id), wasAlreadyInCart: !owned })
      }
      if ((existing as any).isGift !== true) (existing as any).isGift = true
      return true
    }

    // Берём первый доступный подарок из цепочки (план Б при стопе основного).
    for (const giftId of chain) {
      const product = findGiftProduct(giftId)
      if (!product || product.stop_list === true) continue

      await cartStore.addToCart(
        {
          id: String(product.id),
          name: product.name,
          price: Number(product.price) || 0,
          img: product.img || [],
          category_id: product.category_id,
          itemType: 'product',
          productId: String(product.id),
          isGift: true,
          quantity: 1,
        } as any,
        branchId,
      )
      setGiftState({ productId: giftId, wasAlreadyInCart: false })
      consola.success('🎁 [Promocode] Подарок добавлен в корзину:', product.name)
      return true
    }

    // Ни один подарок из цепочки не доступен.
    consola.warn('🎁 [Promocode] Все подарки цепочки недоступны:', chain)
    return false
  }

  /** Убирает подарок при снятии промокода: авто-добавленный — удаляем, «свой» — только метку. */
  function removeGiftFromCart(): void {
    const gift = giftState.value ?? readGiftOwnership()
    giftState.value = null
    if (!gift) {
      writeGiftOwnership(null)
      return
    }

    const item = cartStore.cart.find(i => String(i.id) === gift.productId)
    if (!item) {
      // Запись снимаем, только если корзина ТОЧНО без подарка. Пустая корзина ничего
      // не доказывает — на старте она пуста, пока не приехала с сервера.
      if (cartStore.cart.length > 0) writeGiftOwnership(null)
      return
    }

    writeGiftOwnership(null)

    if (gift.wasAlreadyInCart) {
      ;(item as any).isGift = false
      return
    }

    const branchId = Number(institutionStore.selectedBranch?.id)
    if (!branchId) return
    void cartStore.removeFromCart(item.id, branchId, (item as any).addons ?? [], (item as any).excludes ?? [])
  }

  /**
   * Убирает подарок, оставшийся от промокода прошлой сессии.
   *
   * Промокод после перезагрузки НЕ восстанавливается, а подарочная позиция живёт на
   * сервере и приезжает обычной платной строкой — гость платил за подарок, просто
   * обновив страницу. Раз промокода больше нет, нет и подарка.
   *
   * Ждём именно НЕПУСТУЮ корзину: пустая на старте означает «ещё не загрузилась»,
   * и снимать по ней владение нельзя (та же гонка, что у подарка за сумму).
   */
  function releaseOrphanGiftAfterReload(): void {
    if (!import.meta.client) return
    if (isApplied.value || giftState.value !== null) return

    const remembered = readGiftOwnership()
    if (!remembered) return
    if (cartStore.cart.length === 0) return

    const item = cartStore.cart.find(i => String(i.id) === remembered.productId)
    if (!item) {
      writeGiftOwnership(null)
      return
    }

    consola.info('🎁 [Promocode] Подарок прошлой сессии снят: промокод больше не применён')
    giftState.value = remembered
    removeGiftFromCart()
  }

  // Корзина приезжает с сервера асинхронно, поэтому сверяемся при каждом её изменении,
  // а не один раз при создании композабла.
  watch(
    () => cartStore.cart.length,
    () => releaseOrphanGiftAfterReload(),
    { immediate: true },
  )

  /**
   * Получает тип заказа из формы
   */
  const orderType = computed((): 'delivery' | 'takeaway' | 'dine_in' => {
    const method = checkoutFormStore.OrderForm.method_receipt
    if (method === 'delivery') return 'delivery'
    if (method === 'takeaway') return 'takeaway'
    return 'dine_in'
  })

  /**
   * Собирает ID продуктов из корзины
   */
  const productIds = computed((): string[] => {
    return cartStore.cart.map(item => String(item.id))
  })

  /**
   * Собирает детали товаров для промокодов (productId, price, quantity, categoryId)
   */
  const orderItems = computed((): OrderItemForPromocode[] => {
    return cartStore.cart.map(item => ({
      productId: String(item.id),
      price: Number(item.price),
      quantity: Number(item.quantity),
      categoryId: item.category_id, // ← Для промокодов на категории товаров
    }))
  })

  /**
   * Собирает ID категорий из продуктов (если они есть в данных)
   */
  const categoryIds = computed((): string[] => {
    // Предполагаем, что у товаров может быть category_id
    const categories = cartStore.cart
      .map(item => (item as any).category_id)
      .filter(Boolean)
      .map(String)
    return [...new Set(categories)] // Уникальные значения
  })

  /**
   * Получает IP-адрес клиента (упрощенная версия)
   */
  function getClientIpAddress(): string | undefined {
    // Можно использовать внешний сервис или получить из заголовков на сервере
    return undefined // Заполнится на сервере при необходимости
  }

  /**
   * Получает ID устройства
   */
  function getDeviceId(): string | undefined {
    if (process.client) {
      let deviceId = localStorage.getItem('deviceId')
      if (!deviceId) {
        deviceId = `device-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
        localStorage.setItem('deviceId', deviceId)
      }
      return deviceId
    }
    return undefined
  }

  /**
   * Валидирует промокод
   * @param forceLoyaltyValue - Принудительное значение для isUsingLoyalty (для retry без баллов)
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity -- валидация промокода и разбор ответа API
  async function validatePromocode(
    forceLoyaltyValue?: boolean,
    options?: { silent?: boolean },
  ): Promise<boolean> {
    // Определяем используются ли баллы (из параметра или реактивного состояния)
    const useLoyaltyPointsValue = forceLoyaltyValue === undefined ? false : forceLoyaltyValue
    const silent = options?.silent === true
    
    consola.debug('🎟️ [Promocode] validatePromocode called', {
      promocode: promocode.value,
      useLoyaltyPoints: useLoyaltyPointsValue,
      forcedValue: forceLoyaltyValue,
    })

    if (!promocode.value.trim()) {
      error.value = 'Введите промокод'
      consola.error('❌ [Promocode] Empty promocode')
      return false
    }

    if (!isLoggedIn.value || !userProfileStore.getUserId) {
      error.value = 'Необходимо авторизоваться'
      consola.error('❌ [Promocode] User not authenticated', {
        isLoggedIn: isLoggedIn.value,
        userId: userProfileStore.getUserId,
      })
      return false
    }

    consola.debug('✅ [Promocode] Initial checks passed, preparing payload...')

    isLoading.value = true
    error.value = null
    const finish = (result: boolean): boolean => {
      isLoading.value = false

      // Тихая ре-валидация (пересчёт при смене корзины) — не действие пользователя,
      // в воронку она не идёт: иначе один введённый промокод дал бы десяток событий.
      if (!silent) {
        track('checkout_promocode', {
          code: promocode.value.toUpperCase(),
          ok: result,
          reason: result ? undefined : (error.value ?? undefined),
        })
      }

      return result
    }

    try {
      const payload: ValidatePromocodeDto = {
        code: promocode.value.toUpperCase(),
        userId: String(userProfileStore.getUserId),
        institutionId: String(institutionStore.institution?.id || ''),
        orderAmount: promocodeBaseAmount.value,
        orderType: orderType.value,
        branchId: String(institutionStore.selectedBranch?.id || ''),
        zoneId: checkoutFormStore.OrderForm.delivery_zone_id || undefined,
        productIds: productIds.value,
        items: orderItems.value, // ← Детали товаров для product_discount
        categoryIds: categoryIds.value.length > 0 ? categoryIds.value : undefined,
        isUsingLoyalty: useLoyaltyPointsValue,
      }

      consola.debug('📦 [Promocode] Payload prepared:', payload)
      consola.debug('🚀 [Promocode] Sending request to /api/v2/promocodes/validate...')

      let response
      try {
        const apiResponse = await api.post<{ success: boolean; data: PromocodeResult }>('/promocodes/validate', {
          body: payload,
        })
        response = apiResponse.data
        consola.debug('📨 [Promocode] Response received:', response)
      } catch (apiError: any) {
        consola.error('❌ [Promocode] API request failed:', apiError)
        throw apiError // Пробросим дальше в основной catch
      }

      consola.debug('🔍 [Promocode] Checking response:', {
        hasResponse: !!response,
        responseType: typeof response,
        success: response?.success,
        hasData: !!response?.data,
        fullResponse: JSON.stringify(response, null, 2),
      })

      if (response.success && response.data) {
        // Проверяем, валиден ли промокод
        if (response.data.valid) {
          // 🎁 gift_product: СНАЧАЛА пробуем положить подарок (первый доступный из цепочки).
          // Если недоступна вся цепочка (основной + план Б) — промокод НЕ применяем,
          // чтобы не было тупика на кассе (подарка нет, а заказ без него не проходит).
          // Делаем это ДО isApplied=true, поэтому watcher ре-валидации не сработает.
          const giftPlaced = await syncGiftInCart(response.data)
          if (!giftPlaced) {
            const msg = 'Подарок по промокоду сейчас недоступен. Попробуйте позже.'
            error.value = msg
            if (!silent) alertStore.error(msg)
            return finish(false)
          }

          // Сохраняем результат валидации как примененный промокод
          appliedPromocode.value = response.data
          isApplied.value = true
          error.value = null

          // Сохраняем промокод в форму заказа для последующего apply
          checkoutFormStore.setField('promocodeId', promocode.value.toUpperCase())

          // 🎉 Показываем тост с информацией о применении (не на тихой ре-валидации)
          if (!silent) {
            const benefits = []
            if (response.data.discountType === 'gift_product') {
              benefits.push('Подарок добавлен в корзину')
            }
            if (response.data.discountAmount > 0) {
              benefits.push(`Скидка ${response.data.discountAmount}₽`)
            }
            if (response.data.freeDelivery) {
              benefits.push('Бесплатная доставка')
            }
            const benefitsText = benefits.length > 0 ? benefits.join(', ') : 'Применен'
            alertStore.success(`✅ Промокод применен! ${benefitsText}`)
          }

          consola.debug('✅ [Promocode] Successfully applied:', {
            discount: response.data.discountAmount,
            freeDelivery: response.data.freeDelivery,
            compatibleWithLoyalty: response.data.compatibleWithLoyalty,
          })
          
          return finish(true)
        } else {
          // ⚠️ Промокод невалиден
          const errorMsg = response.data.errorMessage || 'Промокод не валиден'
          
          // ✅ Проверяем несовместимость с баллами (из ответа или парсинг errorMessage)
          const compatibleWithLoyalty = response.data.compatibleWithLoyalty ?? 
                                       !(errorMsg.includes('баллами лояльности') || errorMsg.includes('loyalty'))
          
          // 🔄 АВТОМАТИЧЕСКАЯ ПЕРЕВАЛИДАЦИЯ: Если несовместим с баллами И баллы используются
          // Проверяем что это был первый запрос С баллами (forceLoyaltyValue не был передан или был true)
          const wasUsingLoyalty = forceLoyaltyValue === undefined || forceLoyaltyValue === true
          
          if (!compatibleWithLoyalty && wasUsingLoyalty) {
            consola.warn('🔄 [Promocode] Несовместим с баллами, пробуем БЕЗ баллов...')
            
            // Сообщаем пользователю что мы делаем
            alertStore.info('Промокод несовместим с баллами. Применяем без баллов...')
            
            // Рекурсивно вызываем валидацию БЕЗ баллов (forceLoyaltyValue = false)
            // Это запустит перевалидацию автоматически
            const retryResult = await validatePromocode(false)
            
            if (retryResult) {
              // ✅ Промокод валиден БЕЗ баллов!
              consola.debug('✅ [Promocode] Применен после отключения баллов')
              return finish(true)
            } else {
              // ❌ Даже без баллов не валиден (другая ошибка)
              consola.error('❌ [Promocode] Невалиден даже без баллов')
              return finish(false)
            }
          }
          
          // Обычная ошибка валидации (не связанная с баллами)
          error.value = errorMsg
          alertStore.error(errorMsg)
          consola.error('❌ [Promocode] Validation failed:', errorMsg)
          return finish(false)
        }
      }

      error.value = 'Промокод не валиден'
      alertStore.error('Промокод не валиден')
      consola.error('❌ [Promocode] Validation failed, response:', response)
      return finish(false)
    } catch (error_: any) {
      consola.error('❌ [Promocode] Validation error caught:', error_)
      consola.error('❌ [Promocode] Error type:', typeof error_)
      consola.error('❌ [Promocode] Error keys:', Object.keys(error_ || {}))
      
      const errorData = error_?.data as PromocodeError | undefined
      const errorMsg = errorData?.message || 'Ошибка валидации промокода'
      error.value = errorMsg
      alertStore.error(errorMsg)
      return finish(false)
    }
  }

  /**
   * Применяет промокод (фактическое применение с записью использования)
   * Вызывается при оформлении заказа
   */
  async function applyPromocodeToOrder(orderId: string, useLoyaltyPoints: boolean = false): Promise<boolean> {
    if (!promocode.value.trim()) {
      error.value = 'Введите промокод'
      return false
    }

    if (!isLoggedIn.value || !userProfileStore.getUserId) {
      error.value = 'Необходимо авторизоваться'
      return false
    }

    isLoading.value = true
    error.value = null
    const finish = (result: boolean): boolean => {
      isLoading.value = false
      return result
    }

    try {
      const payload: ApplyPromocodeDto = {
        code: promocode.value.toUpperCase(),
        userId: String(userProfileStore.getUserId),
        orderId,
        institutionId: String(institutionStore.institution?.id || ''),
        orderAmount: promocodeBaseAmount.value,
        orderType: orderType.value,
        branchId: String(institutionStore.selectedBranch?.id || ''),
        zoneId: checkoutFormStore.OrderForm.delivery_zone_id || undefined,
        productIds: productIds.value,
        items: orderItems.value, // ← Детали товаров для product_discount
        categoryIds: categoryIds.value.length > 0 ? categoryIds.value : undefined,
        isUsingLoyalty: useLoyaltyPoints,
        // ipAddress сервер больше не берёт из тела — определяет сам по запросу.
        // Поле оставлено, чтобы не ломать контракт со старыми сборками приложения.
        ipAddress: await getClientIpAddress(),
        deviceId: getDeviceId(),
        // Настоящий отпечаток устройства: именно по нему сервер считает лимит
        // max_usage_per_device. deviceId выше — случайная строка из localStorage,
        // которая сбрасывается очисткой данных сайта.
        clientFingerprint: await getClientFingerprint(),
        userAgent: process.client ? navigator.userAgent : undefined,
      }

      const apiResponse = await api.post<{ success: boolean; data: PromocodeResult }>('/promocodes/apply', {
        body: payload,
      })
      const response = apiResponse.data

      if (response.success) {
        // Обновляем данные после фактического применения
        appliedPromocode.value = response.data
        return finish(true)
      }

      error.value = 'Не удалось применить промокод'
      return finish(false)
    } catch (error_: any) {
      const errorData = error_?.data as PromocodeError | undefined
      error.value = errorData?.message || 'Ошибка применения промокода'
      isApplied.value = false
      appliedPromocode.value = null
      return finish(false)
    }
  }

  /**
   * Сбрасывает промокод
   */
  function resetPromocode(): void {
    consola.debug('🔄 [Promocode] Resetting promocode')
    promocode.value = ''
    isApplied.value = false
    error.value = null
    appliedPromocode.value = null

    // Убираем промокод из формы заказа
    checkoutFormStore.setField('promocodeId', null)

    // Убираем подарок ПОСЛЕ сброса isApplied (watcher ре-валидации уже не сработает).
    removeGiftFromCart()
  }

  /**
   * Автоматически перевалидирует промокод при изменении корзины
   * Предотвращает абьюз (изменение суммы после применения промокода)
   */
  async function revalidateIfNeeded(): Promise<void> {
    if (!isApplied.value) return // Промокод не применен - ничего не делаем
    
    consola.debug('🔄 [Promocode] Cart changed, revalidating promocode...')
    
    // Перевалидируем промокод (используем текущее состояние баллов лояльности)
    // silent: не показываем тост «Промокод применён» на каждое изменение корзины.
    const useLoyalty = false // Используем false т.к. баллы будут управляться через watch
    const isValid = await validatePromocode(useLoyalty, { silent: true })
    
    if (!isValid) {
      // Промокод больше не валиден - сбрасываем
      consola.warn('⚠️ [Promocode] No longer valid after cart change, resetting')
      resetPromocode()
    }
  }

  /**
   * Получает скидку от промокода
   */
  const discountAmount = computed(() => {
    return appliedPromocode.value?.discountAmount || 0
  })

  /**
   * Проверяет, дает ли промокод бесплатную доставку
   */
  const isFreeDelivery = computed(() => {
    return appliedPromocode.value?.freeDelivery || false
  })

  /**
   * Получает финальную сумму с учетом промокода
   */
  const finalAmount = computed(() => {
    return appliedPromocode.value?.finalAmount || cartStore.totalPrice
  })

  /**
   * Получает ID примененного промокода
   */
  const appliedPromocodeId = computed(() => {
    return isApplied.value ? promocode.value : null
  })

  /**
   * Проверяет, совместим ли промокод с программой лояльности
   */
  const isCompatibleWithLoyalty = computed(() => {
    // Если промокод не применен, то совместимость не имеет значения
    if (!isApplied.value || !appliedPromocode.value) return true
    
    // ✅ Проверяем поле compatibleWithLoyalty (должно прийти с бэкенда)
    // Если false - несовместим, иначе - совместим (по умолчанию true)
    const compatible = appliedPromocode.value.compatibleWithLoyalty !== false
    
    consola.debug('🔍 [Promocode] Compatibility check:', {
      isApplied: isApplied.value,
      compatibleWithLoyalty: appliedPromocode.value.compatibleWithLoyalty,
      result: compatible,
    })
    
    return compatible
  })

  // ✅ Watch для автоматической перевалидации при изменении корзины
  // Отслеживаем изменения товаров, количества и цен
  watch(
    () => ({
      items: cartStore.cart.map(item => ({
        id: item.id,
        quantity: item.quantity,
        price: item.price,
      })),
      totalPrice: cartStore.totalPrice,
    }),
    async (newValue, oldValue) => {
      // Проверяем что корзина действительно изменилась
      const hasChanges = Boolean(oldValue) && (
        JSON.stringify(newValue.items) !== JSON.stringify(oldValue.items) ||
        newValue.totalPrice !== oldValue.totalPrice
      )
      
      if (hasChanges && isApplied.value && oldValue) {
        consola.debug('🛒 [Promocode] Cart changed, triggering revalidation', {
          oldTotal: oldValue.totalPrice,
          newTotal: newValue.totalPrice,
          oldItems: oldValue.items.length,
          newItems: newValue.items.length,
        })
        
        await revalidateIfNeeded()
      }
    },
    { deep: true },
  )

  return {
    // State
    promocode,
    isApplied,
    isLoading,
    error,
    appliedPromocode,

    // Computed
    discountAmount,
    isFreeDelivery,
    finalAmount,
    appliedPromocodeId,
    orderType,
    isCompatibleWithLoyalty,

    // Actions
    validatePromocode,
    applyPromocodeToOrder,
    resetPromocode,
    revalidateIfNeeded, // Экспортируем для ручной перевалидации при необходимости
  }
}


